export interface Project {
  id: string;
  name: string;
  category: string;
  title: string;
  summary: string;
  period: string;
  role: string;
  stack: string[];
  /** Shown as "Results" at the top of the case study. ==x== highlights. */
  highlights: string[];
  sections: { title: string; text: string }[];
}

export const projects: Project[] = [
  {
    id: "cloudsway",
    name: "Cloudsway",
    category: "Data quality & internal tools",
    title: "Multimodal data curation at Cloudsway.",
    summary:
      "A verified migration, a searchable workbench, and quality checks that separate hard failures from cases needing human review.",
    period: "May–August 2026",
    role: "AI Data Quality & Automation Intern",
    stack: ["Python", "FastAPI", "DuckDB", "Multimodal evaluation"],
    highlights: [
      "Reconciled a migration of ==29,356 objects== (78.46 GiB): 28,727 copied, 627 duplicates skipped, 2 excluded.",
      "Built an internal data workbench with SQL and natural-language queries, media previews, and manifest export, available to about 30–40 colleagues.",
      "Designed layered quality checks used to evaluate 50 multimodal demos.",
    ],
    sections: [
      {
        title: "The problem",
        text: "The collection spanned different suppliers, formats, and modalities. Folders alone could not answer which samples contained video and IMU, which were ready for delivery, or which still lacked required metadata. The work needed both an accountable migration and a way for colleagues to inspect the result.",
      },
      {
        title: "My contribution",
        text: "I directed the AI-assisted implementation and validation of the curation pipeline and internal workbench. I translated requirements into checks, inspected outputs, corrected assumptions, and prepared handoffs. The workbench combined SQL and Gemini-assisted natural-language queries with media previews, quality triage, and manifest export.",
      },
      {
        title: "Account for every object",
        text: "The source inventory contained 29,356 objects, totaling 78.46 GiB. The plan marked 28,727 for copying, skipped 627 duplicates, and excluded one junk file and one redundant archive. Idempotent execution allowed safe restarts. A 28-check dry run included retrieval queries; the migration was then executed and verified.",
      },
      {
        title: "Designing the quality checks",
        text: "I designed QC workflows that separated deterministic failures from perceptual signals, LLM review, and human sampling. For browser workflows, I challenged a rule that treated the maximum exportable frame count as the required screenshot count, then checked it against the source requirements. A strict check is only useful if it checks the right thing.",
      },
      {
        title: "What was delivered",
        text: "The data workbench was deployed internally and made available to approximately 30–40 colleagues. Multimodal evaluation covered 50 demos across UI generation, browser workflows, and code generation. Availability is documented; ongoing usage was not measured. This case study uses aggregate results because the underlying data and tools are internal.",
      },
    ],
  },
  {
    id: "smarteats",
    name: "SmartEats",
    category: "Applied AI & product development",
    title: "Building SmartEats for campus dining.",
    summary:
      "UIUC menus, meal tracking, and grounded AI assistance—with 375 model evaluations and a separate retrieval experiment behind the design.",
    period: "August 2025–present",
    role: "Product architecture, AI-assisted development & evaluation",
    stack: ["React", "TypeScript", "Django REST", "PostgreSQL", "Gemini"],
    highlights: [
      "Covers seven UIUC dining halls and 580+ dishes, with menus fetched daily.",
      "Ran ==375 evaluations== across 15 open-source models and five task types to inform a model-routing design.",
      "Compared three embedding models and three chunking strategies in a separate 45-run retrieval experiment.",
    ],
    sections: [
      {
        title: "The problem",
        text: "Choosing a meal starts with what a dining hall actually serves. SmartEats brings menu discovery, nutrition estimates, meal logging, and AI assistance into one application, covering seven UIUC dining halls and more than 580 dishes in the documented project snapshot.",
      },
      {
        title: "My contribution",
        text: "I shaped the product and architecture, directed AI coding tools through implementation and debugging, and evaluated model choices. The React/TypeScript frontend connects to a Django REST backend. A daily pipeline fetches menus and enriches dish nutrition using Gemini; database-backed meal context constrains the assistant to the available food data.",
      },
      {
        title: "Evaluate by task",
        text: "The model experiment covers 15 open-source models, five task types, and five prompts for each model/task pair: 375 evaluations. I compared recorded scores and latency, then used the findings to design speed, quality, balanced, and API-fallback routing tiers. The experiment and routing proposal are distinct from the live application.",
      },
      {
        title: "Retrieval is a separate decision",
        text: "A second experiment compared three embedding models and three chunking strategies across 45 runs. MPNet-768D with semantic-tag chunking offered a useful quality/cost tradeoff in that small experiment. The application uses MiniLM for semantic dish search. MPNet’s result belongs to the separate retrieval experiment.",
      },
      {
        title: "Reading the results",
        text: "The chart uses the experiment’s recorded self-evaluation scores. With five prompts per model in each task, it is a small comparison, and the latency reflects the run environment. I used it to inform project decisions. Independent evaluation and a broader prompt set would be needed to generalize the findings. AI-enriched nutrition remains an estimate.",
      },
    ],
  },
  {
    id: "equota",
    name: "EQuota Energy",
    category: "Forecasting & data pipelines",
    title: "Electricity demand forecasting at EQuota.",
    summary:
      "Rolling-window validation for electricity demand, followed by weather-data pipelines and regional bias analysis.",
    period: "May–November 2024 / May–August 2025",
    role: "Algorithm Engineer Intern / Machine Learning Engineer Intern",
    stack: [
      "Python",
      "pandas",
      "XGBoost",
      "LightGBM",
      "Time-series validation",
    ],
    highlights: [
      "Cut validation MAPE from ==9.3% to 5.6%== across three pilot sites, roughly 40% relative.",
      "Automated ingestion of 20,000+ daily weather-forecast files, from hours to minutes.",
      "Traced systematic forecast bias of up to 15% across nine or more regions.",
    ],
    sections: [
      {
        title: "The problem",
        text: "Short-term electricity demand varies with weather, recent load, and calendar effects. A forecast needs to be evaluated across time, and its inputs need to arrive consistently enough for the evaluation to stay useful.",
      },
      {
        title: "My contribution",
        text: "In 2024, I benchmarked ARIMA, XGBoost, and LightGBM, engineered lag and rolling features, and used rolling-window backtesting with hyperparameter search. Validation MAPE fell from 9.3% to 5.6% across three pilot sites, a relative reduction of approximately 40%.",
      },
      {
        title: "The data pipeline mattered too",
        text: "Returning in 2025, I built scheduled Python/pandas ingestion and preprocessing for more than 20,000 daily weather-forecast files, reducing latency from hours to minutes. I also analyzed load and photovoltaic anomalies across nine or more regions, identifying systematic forecasting bias of up to 15% to inform corrections and rule-based filtering.",
      },
      {
        title: "Monitoring and handoff",
        text: "Monitoring dashboards showed performance under weather-driven demand shifts. I produced reports highlighting demand anomalies and peak-use patterns, and mentored three interns on preprocessing and pipeline-quality standards. The reported MAPE comes from validation across the three pilot sites. Production cost savings were outside the scope of that result.",
      },
    ],
  },
];
